import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  urlOrOptions: string | { url: string; method?: string; body?: unknown; signal?: AbortSignal },
  methodOrData?: string | unknown,
  data?: unknown
): Promise<any> {
  let url: string;
  let method: string = 'GET';
  let body: unknown | undefined;
  let signal: AbortSignal | undefined;
  
  // Handle different parameter formats
  if (typeof urlOrOptions === 'string') {
    url = urlOrOptions;
    if (typeof methodOrData === 'string') {
      method = methodOrData;
      body = data;
    } else {
      body = methodOrData;
    }
  } else {
    url = urlOrOptions.url;
    method = urlOrOptions.method || 'GET';
    body = urlOrOptions.body;
    signal = urlOrOptions.signal;
  }

  // Fix for using HTTP method in fetch - explicitly using method for fetch requests
  const fetchOptions: RequestInit = {
    method: method,
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
    signal: signal || undefined,
  };

  try {
    const res = await fetch(url, fetchOptions);
    
    // Check if there's content before trying to parse as JSON
    const contentType = res.headers.get('content-type');
    const hasJsonContent = contentType && contentType.includes('application/json');
    
    // If the response is not ok, handle the error differently
    if (!res.ok) {
      if (hasJsonContent) {
        // Try to extract the error message from JSON response
        try {
          // Clone the response before reading to avoid the "body already read" error
          const errorRes = res.clone();
          const errorData = await errorRes.json();
          throw new Error(errorData.message || `API error: ${res.status} ${res.statusText}`);
        } catch (jsonError) {
          // If JSON parsing fails, use status code
          throw new Error(`API error: ${res.status} ${res.statusText}`);
        }
      } else {
        // Non-JSON error response
        throw new Error(`API error: ${res.status} ${res.statusText}`);
      }
    }
    
    // Success case - return JSON if content type is JSON, otherwise return response
    if (hasJsonContent) {
      try {
        return await res.json();
      } catch (e) {
        console.error('Error parsing JSON response:', e);
        throw new Error('Failed to parse JSON response from server');
      }
    } else {
      return res;
    }
  } catch (error) {
    console.error(`API request error for ${method} ${url}:`, error);
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Check if the first element in queryKey is a string
    if (typeof queryKey[0] !== 'string') {
      throw new Error(`Invalid queryKey: ${JSON.stringify(queryKey)}`);
    }

    const url = queryKey[0] as string;
    
    try {
      const res = await fetch(url, {
        method: 'GET',
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }
      
      // Check if there's content before trying to parse as JSON
      const contentType = res.headers.get('content-type');
      const hasJsonContent = contentType && contentType.includes('application/json');
      
      // If the response is not ok, handle the error differently
      if (!res.ok) {
        if (hasJsonContent) {
          try {
            // Clone the response before reading to avoid the "body already read" error
            const errorRes = res.clone();
            const errorData = await errorRes.json();
            throw new Error(errorData.message || `API error: ${res.status} ${res.statusText}`);
          } catch (jsonError) {
            throw new Error(`API error: ${res.status} ${res.statusText}`);
          }
        } else {
          throw new Error(`API error: ${res.status} ${res.statusText}`);
        }
      }
      
      // Success case - parse JSON
      try {
        return await res.json();
      } catch (e) {
        console.error('Error parsing JSON response:', e);
        throw new Error('Failed to parse JSON response from server');
      }
    } catch (error) {
      console.error(`Failed to fetch ${url}:`, error);
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
